'use client';

import Link from 'next/link';
import {
  Home,
  ImagePlus,
  MessageSquare,
  User
} from 'lucide-react';
import { useRouter, usePathname } from 'next/navigation';
import { useState } from 'react';
import { useAuth } from '@/app/user-and-session/session-auth/hooks/useAuth';
import LoginPopup from '@/app/popup/login-popup';
import { useUserAppData } from '@/app/user-and-session/user-app-data/useUserAppData';

type MobileNavItem = {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  href?: string;
  requiresAuth?: boolean;
  onClick?: () => void;
};

export default function MobileBottomNav() {
  const { userEmail, userName, isLoading: authLoading, isAuthenticated } = useAuth();
  const [userAppData] = useUserAppData(
    !authLoading && isAuthenticated && userEmail ? userEmail : ''
  );
  const router = useRouter();
  const pathname = usePathname();
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [loginRedirectUrl, setLoginRedirectUrl] = useState('/');
  const [clickedItem, setClickedItem] = useState<string | null>(null);

  const handleAuthRequiredAction = (href: string, action?: () => void) => {
    if (!userAppData.isInitialized) {
      setLoginRedirectUrl(href);
      setShowLoginPopup(true);
      return;
    }
    
    if (action) {
      action();
    } else {
      router.push(href);
    }
  };

  const navItems: MobileNavItem[] = [
    {
      icon: Home,
      label: 'Home',
      href: '/discover',
    },
    {
      icon: MessageSquare,
      label: 'Chats',
      href: '/my-chat',
    },
    {
      icon: ImagePlus,
      label: 'Create',
      href: '/image-generation',
      requiresAuth: true,
    },
    {
      icon: User,
      label: 'Profile',
      href: '/profile',
      requiresAuth: true,
    },
  ];

  const isActiveRoute = (href?: string) => {
    if (!href) return false;
    return pathname === href || pathname.startsWith(href);
  };

  const handleItemClick = (item: MobileNavItem) => {
    // Immediately set the clicked item for instant visual feedback
    setClickedItem(item.label);
    
    if (item.requiresAuth) {
      handleAuthRequiredAction(item.href || '/', item.onClick);
    } else if (item.onClick) {
      item.onClick();
    } else if (item.href) {
      router.push(item.href);
    }
  };

  return (
    <>
      {/* Mobile Bottom Navigation */}
      <div className="bg-[#1a1a1e] border-t border-gray-800 z-50 w-full flex-shrink-0" style={{ paddingBottom: 'var(--safe-area-inset-bottom)' }}>
        <div className="flex items-center justify-around px-2 py-2 h-12">
          {navItems.map((item) => {
            const isActive = isActiveRoute(item.href);
            const isClicked = clickedItem === item.label;
            
            return (
              <button
                key={item.label}
                onClick={() => handleItemClick(item)}
                className={`
                  flex flex-col items-center justify-center px-4 py-2 rounded-lg transition-colors
                  ${isActive || isClicked
                    ? 'text-purple-200' 
                    : 'text-gray-400 hover:text-purple-200'
                  }
                `}
              >
                <item.icon className="w-7 h-7 [stroke-width:2.2] -mt-1.5 mb-1" />
              </button>
            );
          })}
        </div>
      </div>

      {/* Login Popup */}
      <LoginPopup 
        isOpen={showLoginPopup} 
        onClose={() => {
          setShowLoginPopup(false);
        }}
        redirectTarget={loginRedirectUrl}
      />
    </>
  );
} 